import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  FaArrowLeft,
  FaArrowRight,
  FaCamera,
  FaCheck,
  FaMapMarkerAlt,
  FaSignInAlt
} from 'react-icons/fa';

import '../css/Register.css';
import backgroundIllustration from '../assets/couple.png';
import logo1 from '../assets/logo1.png';

const MAX_PHOTOS = 6;
const MAX_PHOTO_SIZE = 8 * 1024 * 1024;

function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const locationRequestedRef = useRef(false);
  const manualGeocodingRef = useRef(null);

  const [step, setStep] = useState(1);
  const [locationDetected, setLocationDetected] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationAccuracy, setLocationAccuracy] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [ageError, setAgeError] = useState('');
  const [photoPreviews, setPhotoPreviews] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    age: '',

    location: '',
    city: '',
    state: '',
    country: '',
    latitude: null,
    longitude: null,

    maritalStatus: '',
    hasChildren: false,
    gender: '',
    termsAccepted: false,
    skinTone: '',
    mutualLike: false,
    profilePicture: [],
    bio: ''
  });

  const currentLanguage = useMemo(
    () => (i18n.language?.startsWith('fr') ? 'fr' : 'en'),
    [i18n.language]
  );

  const updateFormData = (changes) => {
    setFormData((previous) => ({
      ...previous,
      ...changes
    }));
  };

  const buildLocationFields = (address = {}) => {
    const city =
      address.city ||
      address.town ||
      address.village ||
      address.hamlet ||
      address.municipality ||
      address.suburb ||
      '';

    const state =
      address.state ||
      address.region ||
      address.state_district ||
      '';

    const country = address.country || '';

    return {
      city,
      state,
      country,
      location: [city, state, country]
        .filter(Boolean)
        .join(', ')
    };
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
      files
    } = event.target;

    setErrorMessage('');

    if (type === 'file') {
      const selectedFiles = Array.from(files || []);

      if (selectedFiles.length > MAX_PHOTOS) {
        setErrorMessage(
          t('register.maxPhotosError', {
            count: MAX_PHOTOS,
            defaultValue: `Vous pouvez sélectionner jusqu’à ${MAX_PHOTOS} photos.`
          })
        );
        return;
      }

      const invalidFile = selectedFiles.find(
        (file) =>
          ![
            'image/jpeg',
            'image/png',
            'image/webp'
          ].includes(file.type) ||
          file.size > MAX_PHOTO_SIZE
      );

      if (invalidFile) {
        setErrorMessage(
          t(
            'register.photoFormatError',
            'Utilisez uniquement JPG, PNG ou WEBP, avec 8 MB maximum par photo.'
          )
        );
        return;
      }

      updateFormData({
        profilePicture: selectedFiles
      });

      photoPreviews.forEach((url) =>
        URL.revokeObjectURL(url)
      );

      setPhotoPreviews(
        selectedFiles.map((file) =>
          URL.createObjectURL(file)
        )
      );

      return;
    }

    if (type === 'checkbox') {
      updateFormData({
        [name]: checked
      });
      return;
    }

    if (name === 'age') {
      const numericAge = Number(value);

      if (value && numericAge < 18) {
        setAgeError(
          t(
            'register.ageError',
            'Vous devez avoir au moins 18 ans.'
          )
        );
      } else if (value && numericAge > 100) {
        setAgeError(
          t(
            'register.ageMaximumError',
            'Veuillez entrer un âge valide.'
          )
        );
      } else {
        setAgeError('');
      }
    }

    if (name === 'location') {
      setLocationDetected(false);
      setLocationAccuracy(null);

      updateFormData({
        location: value,
        city: '',
        state: '',
        country: '',
        latitude: null,
        longitude: null
      });

      return;
    }

    updateFormData({
      [name]: value
    });
  };

  const requestBrowserPosition = (options) => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        options
      );
    });
  };

  const reverseGeocode = async (
    latitude,
    longitude
  ) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse` +
      `?lat=${latitude}` +
      `&lon=${longitude}` +
      `&format=json` +
      `&addressdetails=1` +
      `&zoom=18` +
      `&accept-language=${currentLanguage}`
    );

    if (!response.ok) {
      throw new Error(
        `Reverse geocoding failed: ${response.status}`
      );
    }

    const data = await response.json();

    return buildLocationFields(
      data.address || {}
    );
  };

  const detectLocation = async () => {
    if (!navigator.geolocation) {
      setErrorMessage(
        t(
          'register.geolocationUnsupported',
          'La géolocalisation n’est pas disponible sur cet appareil.'
        )
      );
      return;
    }

    if (locationLoading) {
      return;
    }

    setLocationLoading(true);
    setLocationDetected(false);
    setLocationAccuracy(null);
    setErrorMessage('');

    try {
      let position;

      try {
        position =
          await requestBrowserPosition({
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          });
      } catch (highAccuracyError) {
        console.warn(
          '⚠️ High accuracy geolocation failed:',
          highAccuracyError
        );

        position =
          await requestBrowserPosition({
            enableHighAccuracy: false,
            timeout: 30000,
            maximumAge: 600000
          });
      }

      const {
        latitude,
        longitude,
        accuracy
      } = position.coords;

      setLocationAccuracy(
        Math.round(accuracy)
      );

      updateFormData({
        latitude,
        longitude
      });

      try {
        const locationFields =
          await reverseGeocode(
            latitude,
            longitude
          );

        updateFormData({
          ...locationFields,
          latitude,
          longitude
        });

        setLocationDetected(true);
        setErrorMessage('');
      } catch (reverseError) {
        console.error(
          '❌ Reverse geocoding failed:',
          reverseError
        );

        setLocationDetected(true);

        setErrorMessage(
          t(
            'register.locationAddressError',
            'Votre position a été détectée, mais l’adresse n’a pas pu être récupérée. Entrez votre ville manuellement.'
          )
        );
      }
    } catch (error) {
      console.warn(
        '⚠️ Geolocation failed:',
        error
      );

      setLocationDetected(false);
      setLocationAccuracy(null);

      switch (error.code) {
        case 1:
          setErrorMessage(
            t(
              'register.locationPermissionError',
              'L’accès à votre position est bloqué. Autorisez la localisation dans le navigateur, puis cliquez sur Détecter.'
            )
          );
          break;

        case 2:
          setErrorMessage(
            t(
              'register.locationUnavailable',
              'Votre position est temporairement indisponible. Vérifiez le Wi-Fi ou entrez votre adresse.'
            )
          );
          break;

        case 3:
          setErrorMessage(
            t(
              'register.locationTimeout',
              'La détection prend trop de temps. Entrez votre ville et votre État : les coordonnées seront recherchées automatiquement.'
            )
          );
          break;

        default:
          setErrorMessage(
            t(
              'register.locationError',
              'Impossible de détecter votre position. Entrez votre adresse manuellement.'
            )
          );
      }
    } finally {
      setLocationLoading(false);
    }
  };

  useEffect(() => {
    if (locationRequestedRef.current) {
      return;
    }

    locationRequestedRef.current = true;
    detectLocation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const query =
      formData.location.trim();

    if (
      query.length < 5 ||
      locationDetected
    ) {
      return undefined;
    }

    if (manualGeocodingRef.current) {
      window.clearTimeout(
        manualGeocodingRef.current
      );
    }

    manualGeocodingRef.current =
      window.setTimeout(async () => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search` +
            `?format=json` +
            `&addressdetails=1` +
            `&limit=1` +
            `&accept-language=${currentLanguage}` +
            `&q=${encodeURIComponent(query)}`
          );

          if (!response.ok) {
            throw new Error(
              `Forward geocoding failed: ${response.status}`
            );
          }

          const results =
            await response.json();

          if (!results?.length) {
            setErrorMessage(
              t(
                'register.addressNotFound',
                'Adresse introuvable. Ajoutez la ville, l’État et le pays.'
              )
            );
            return;
          }

          const result = results[0];

          const locationFields =
            buildLocationFields(
              result.address || {}
            );

          updateFormData({
            city: locationFields.city,
            state: locationFields.state,
            country: locationFields.country,
            latitude: Number(result.lat),
            longitude: Number(result.lon)
          });

          setLocationAccuracy(null);
          setErrorMessage('');
        } catch (error) {
          console.error(
            '❌ Manual geocoding failed:',
            error
          );
        }
      }, 1200);

    return () => {
      if (manualGeocodingRef.current) {
        window.clearTimeout(
          manualGeocodingRef.current
        );
      }
    };
  }, [
    formData.location,
    locationDetected,
    currentLanguage,
    t
  ]);

  useEffect(() => {
    return () => {
      photoPreviews.forEach((url) =>
        URL.revokeObjectURL(url)
      );
    };
  }, [photoPreviews]);

  const validateStep = () => {
    if (step === 1) {
      if (
        !formData.name.trim() ||
        !formData.age ||
        Number(formData.age) < 18 ||
        Number(formData.age) > 100 ||
        !formData.gender
      ) {
        setErrorMessage(
          t(
            'register.completeBasicInformation',
            'Remplissez correctement votre nom, votre âge et votre genre.'
          )
        );
        return false;
      }
    }

    if (
      step === 2 &&
      !formData.maritalStatus
    ) {
      setErrorMessage(
        t(
          'register.completeRelationshipInformation',
          'Sélectionnez votre situation matrimoniale.'
        )
      );
      return false;
    }

    if (step === 3) {
      const hasValidCoordinates =
        Number.isFinite(
          Number(formData.latitude)
        ) &&
        Number.isFinite(
          Number(formData.longitude)
        );

      if (
        !formData.location.trim() ||
        !formData.bio.trim() ||
        !hasValidCoordinates
      ) {
        setErrorMessage(
          t(
            'register.completeLocationInformation',
            'Ajoutez votre localisation et votre biographie. Attendez aussi que les coordonnées soient trouvées.'
          )
        );
        return false;
      }
    }

    setErrorMessage('');
    return true;
  };

  const nextStep = () => {
    if (!validateStep()) {
      return;
    }

    setStep((previous) =>
      Math.min(previous + 1, 4)
    );

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const previousStep = () => {
    setErrorMessage('');

    setStep((previous) =>
      Math.max(previous - 1, 1)
    );

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailRegex =
      /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

    if (
      !emailRegex.test(
        formData.username.trim()
      )
    ) {
      setErrorMessage(
        t(
          'register.emailError',
          'Veuillez entrer une adresse email valide.'
        )
      );
      return;
    }

    if (formData.password.length < 8) {
      setErrorMessage(
        t(
          'register.passwordLengthError',
          'Le mot de passe doit contenir au moins 8 caractères.'
        )
      );
      return;
    }

    if (
      !formData.profilePicture.length
    ) {
      setErrorMessage(
        t(
          'register.photoRequired',
          'Ajoutez au moins une photo.'
        )
      );
      return;
    }

    if (!formData.termsAccepted) {
      setErrorMessage(
        t(
          'register.termsRequired',
          'Vous devez accepter les conditions et la politique de confidentialité.'
        )
      );
      return;
    }

    const payload = new FormData();

    Object.entries(formData).forEach(
      ([key, value]) => {
        if (key === 'profilePicture') {
          value.forEach((file) => {
            payload.append(
              'profilePicture',
              file
            );
          });
          return;
        }

        if (
          value !== null &&
          value !== ''
        ) {
          payload.append(key, value);
        }
      }
    );

    setSubmitting(true);
    setErrorMessage('');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/register`,
        payload,
        {
          headers: {
            'Content-Type':
              'multipart/form-data'
          }
        }
      );

      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration');
      }

      navigate('/');
    } catch (error) {
      console.error(
        '❌ Registration failed:',
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
        t(
          'register.error',
          'Une erreur est survenue pendant l’inscription.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderError = () =>
    errorMessage ? (
      <div
        className="register-error"
        role="alert"
      >
        {errorMessage}
      </div>
    ) : null;

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <section className="step-content">
            <div className="register-step-heading">
              <span className="register-step-number">
                01
              </span>

              <div>
                <h2>
                  {t(
                    'register.step1Title',
                    'Basic Information'
                  )}
                </h2>

                <p>
                  {t(
                    'register.step1Subtitle',
                    'Présentez-vous en quelques informations.'
                  )}
                </p>
              </div>
            </div>

            {renderError()}

            <div className="register-field">
              <label htmlFor="register-name">
                {t(
                  'register.fullName',
                  'Full Name'
                )}
              </label>

              <input
                id="register-name"
                type="text"
                className="register-control"
                placeholder={t(
                  'register.fullName',
                  'Full Name'
                )}
                name="name"
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
                required
              />
            </div>

            <div className="register-field">
              <label htmlFor="register-age">
                {t(
                  'register.age',
                  'Age'
                )}
              </label>

              <input
                id="register-age"
                type="number"
                min="18"
                max="100"
                inputMode="numeric"
                className="register-control"
                placeholder={t(
                  'register.age',
                  'Age'
                )}
                name="age"
                value={formData.age}
                onChange={handleChange}
                required
              />

              {ageError && (
                <small className="register-field-error">
                  {ageError}
                </small>
              )}
            </div>

            <div className="register-field">
              <label htmlFor="register-gender">
                {t(
                  'register.gender',
                  'Gender'
                )}
              </label>

              <select
                id="register-gender"
                name="gender"
                className="register-control"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">
                  {t(
                    'register.selectOption',
                    'Select'
                  )}
                </option>

                <option value="homme">
                  {t(
                    'register.male',
                    'Male'
                  )}
                </option>

                <option value="femme">
                  {t(
                    'register.female',
                    'Female'
                  )}
                </option>
              </select>
            </div>

            <button
              type="button"
              className="register-primary-button"
              onClick={nextStep}
            >
              {t(
                'register.next',
                'Next'
              )}

              <FaArrowRight />
            </button>

            <RegisterLoginLink t={t} />
          </section>
        );

      case 2:
        return (
          <section className="step-content">
            <div className="register-step-heading">
              <span className="register-step-number">
                02
              </span>

              <div>
                <h2>
                  {t(
                    'register.step2Title',
                    'About You'
                  )}
                </h2>

                <p>
                  {t(
                    'register.step2Subtitle',
                    'Aidez les autres membres à mieux vous connaître.'
                  )}
                </p>
              </div>
            </div>

            {renderError()}

            <div className="register-field">
              <label htmlFor="register-marital-status">
                {t(
                  'register.maritalStatus',
                  'Marital status'
                )}
              </label>

              <select
                id="register-marital-status"
                className="register-control"
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleChange}
                required
              >
                <option value="">
                  {t(
                    'register.selectOption',
                    'Select'
                  )}
                </option>

                <option value="marié">
                  {t(
                    'register.married',
                    'Married'
                  )}
                </option>

                <option value="divorcé">
                  {t(
                    'register.divorced',
                    'Divorced'
                  )}
                </option>

                <option value="célibataire">
                  {t(
                    'register.single',
                    'Single'
                  )}
                </option>
              </select>
            </div>

            <div className="register-field">
              <label htmlFor="register-skin-tone">
                {t(
                  'register.skinTone',
                  'Skin tone'
                )}
              </label>

              <input
                id="register-skin-tone"
                className="register-control"
                placeholder={t(
                  'register.skinTone',
                  'Skin tone'
                )}
                name="skinTone"
                value={formData.skinTone}
                onChange={handleChange}
              />
            </div>

            <label className="register-checkbox-card">
              <input
                type="checkbox"
                name="hasChildren"
                checked={formData.hasChildren}
                onChange={handleChange}
              />

              <span className="register-checkbox-visual">
                <FaCheck />
              </span>

              <span>
                <strong>
                  {t(
                    'register.hasChildren',
                    'I have children'
                  )}
                </strong>

                <small>
                  {t(
                    'register.hasChildrenDescription',
                    'Cette information sera visible sur votre profil.'
                  )}
                </small>
              </span>
            </label>

            <RegisterNavigation
              t={t}
              onPrevious={previousStep}
              onNext={nextStep}
            />

            <RegisterLoginLink t={t} />
          </section>
        );

      case 3:
        return (
          <section className="step-content">
            <div className="register-step-heading">
              <span className="register-step-number">
                03
              </span>

              <div>
                <h2>
                  {t(
                    'register.step3Title',
                    'Location & Bio'
                  )}
                </h2>

                <p>
                  {t(
                    'register.step3Subtitle',
                    'Votre position permet de proposer des profils proches.'
                  )}
                </p>
              </div>
            </div>

            {renderError()}

            <div className="register-field">
              <label htmlFor="register-location">
                {t(
                  'register.location',
                  'Location'
                )}
              </label>

              <div className="register-location-control">
                <FaMapMarkerAlt />

                <input
                  id="register-location"
                  className="register-control"
                  placeholder={t(
                    'register.location',
                    'Location'
                  )}
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                />

                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={locationLoading}
                >
                  {locationLoading
                    ? t(
                      'register.detecting',
                      'Détection...'
                    )
                    : t(
                      'register.detectLocation',
                      'Détecter'
                    )}
                </button>
              </div>

              <small className="register-help-text">
                {locationDetected
                  ? `✅ ${t(
                    'register.locationDetected',
                    'Localisation détectée'
                  )}`
                  : `📌 ${t(
                    'register.enterLocationManually',
                    'Entrez votre ville manuellement si nécessaire.'
                  )}`}
              </small>

              {locationDetected &&
                locationAccuracy && (
                  <small className="register-help-text">
                    ✅ {t(
                      'register.locationAccuracy',
                      'Précision approximative'
                    )}: {locationAccuracy} m
                  </small>
                )}
            </div>

            <div className="register-location-summary">
              <span>
                <strong>
                  {t(
                    'register.city',
                    'City'
                  )}
                </strong>

                {formData.city || '—'}
              </span>

              <span>
                <strong>
                  {t(
                    'register.state',
                    'State'
                  )}
                </strong>

                {formData.state || '—'}
              </span>

              <span>
                <strong>
                  {t(
                    'register.country',
                    'Country'
                  )}
                </strong>

                {formData.country || '—'}
              </span>
            </div>

            <div className="register-field">
              <label htmlFor="register-bio">
                {t(
                  'register.bio',
                  'Bio'
                )}
              </label>

              <textarea
                id="register-bio"
                className="register-control register-textarea"
                placeholder={t(
                  'register.bio',
                  'Bio'
                )}
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="5"
                maxLength="1000"
                required
              />

              <div
                className={`register-character-count ${formData.bio.length > 900
                    ? 'is-near-limit'
                    : ''
                  }`}
              >
                {formData.bio.length} / 1000
              </div>
            </div>

            <RegisterNavigation
              t={t}
              onPrevious={previousStep}
              onNext={nextStep}
            />

            <RegisterLoginLink t={t} />
          </section>
        );

      case 4:
        return (
          <section className="step-content">
            <div className="register-step-heading">
              <span className="register-step-number">
                04
              </span>

              <div>
                <h2>
                  {t(
                    'register.step4Title',
                    'Account & Photos'
                  )}
                </h2>

                <p>
                  {t(
                    'register.step4Subtitle',
                    'Finalisez votre compte et ajoutez vos meilleures photos.'
                  )}
                </p>
              </div>
            </div>

            {renderError()}

            <div className="register-field">
              <label htmlFor="register-email">
                {t(
                  'register.username',
                  'Email'
                )}
              </label>

              <input
                id="register-email"
                type="email"
                className="register-control"
                placeholder={t(
                  'register.username',
                  'Email'
                )}
                name="username"
                value={formData.username}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>

            <div className="register-field">
              <label htmlFor="register-password">
                {t(
                  'register.password',
                  'Password'
                )}
              </label>

              <input
                id="register-password"
                type="password"
                minLength="8"
                className="register-control"
                placeholder={t(
                  'register.password',
                  'Password'
                )}
                name="password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
                required
              />

              <small className="register-help-text">
                {t(
                  'register.passwordHelp',
                  'Au moins 8 caractères.'
                )}
              </small>
            </div>

            <div className="register-photo-section">
              <div className="register-photo-heading">
                <div>
                  <strong>
                    {t(
                      'register.addPhotos',
                      'Add photos'
                    )}
                  </strong>

                  <span>
                    {t(
                      'register.photoHelp',
                      'JPG, PNG ou WEBP — 6 photos maximum.'
                    )}
                  </span>
                </div>

                <span>
                  {formData.profilePicture.length}
                  /{MAX_PHOTOS}
                </span>
              </div>

              <label className="register-photo-button">
                <FaCamera />

                {t(
                  'register.choosePhotos',
                  'Choose photos'
                )}

                <input
                  name="profilePicture"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleChange}
                  hidden
                />
              </label>

              {photoPreviews.length > 0 && (
                <div className="register-photo-grid">
                  {photoPreviews.map(
                    (preview, index) => (
                      <div
                        className="register-photo-preview"
                        key={preview}
                      >
                        <img
                          src={preview}
                          alt={`${t(
                            'register.photo',
                            'Photo'
                          )} ${index + 1}`}
                        />

                        {index === 0 && (
                          <span>
                            {t(
                              'register.mainPhoto',
                              'Main'
                            )}
                          </span>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <label className="register-terms">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleChange}
                required
              />

              <span>
                {t(
                  'register.acceptTerms',
                  'I accept the'
                )}{' '}

                <Link
                  to="/terms"
                  target="_blank"
                >
                  {t(
                    'register.conditions',
                    'terms'
                  )}
                </Link>{' '}

                {t(
                  'register.and',
                  'and'
                )}{' '}

                <Link
                  to="/privacy"
                  target="_blank"
                >
                  {t(
                    'register.privacyPolicy',
                    'privacy policy'
                  )}
                </Link>
              </span>
            </label>

            <div className="register-navigation">
              <button
                type="button"
                className="register-secondary-button"
                onClick={previousStep}
              >
                <FaArrowLeft />

                {t(
                  'register.prev',
                  'Previous'
                )}
              </button>

              <button
                type="submit"
                className="register-primary-button"
                disabled={submitting}
              >
                {submitting
                  ? t(
                    'register.submitting',
                    'Création...'
                  )
                  : t(
                    'register.registerBtn',
                    'Create account'
                  )}
              </button>
            </div>

            <RegisterLoginLink t={t} />
          </section>
        );

      default:
        return null;
    }
  };

  return (
    <main
      className="register-wrapper"
      style={{
        backgroundImage:
          `url(${backgroundIllustration})`
      }}
    >
      <div className="register-overlay" />

      <section className="register-card">
        <div className="register-right">
          <header className="register-header">
            <img
              src={logo1}
              alt="MyHalal"
              className="myhalal-logo"
            />

            <div
              className="step-indicator"
              aria-label="Registration progress"
            >
              {[1, 2, 3, 4].map(
                (number) => (
                  <React.Fragment key={number}>
                    <span
                      className={`step-dot ${number === step
                          ? 'active'
                          : ''
                        } ${number < step
                          ? 'completed'
                          : ''
                        }`}
                    >
                      {number < step
                        ? <FaCheck />
                        : number}
                    </span>

                    {number < 4 && (
                      <span
                        className={`step-line ${number < step
                            ? 'completed'
                            : ''
                          }`}
                      />
                    )}
                  </React.Fragment>
                )
              )}
            </div>
          </header>

          <form
            className="register-form"
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            noValidate
          >
            {renderStep()}
          </form>
        </div>
      </section>
    </main>
  );
}

function RegisterNavigation({
  t,
  onPrevious,
  onNext
}) {
  return (
    <div className="register-navigation">
      <button
        type="button"
        className="register-secondary-button"
        onClick={onPrevious}
      >
        <FaArrowLeft />

        {t(
          'register.prev',
          'Previous'
        )}
      </button>

      <button
        type="button"
        className="register-primary-button"
        onClick={onNext}
      >
        {t(
          'register.next',
          'Next'
        )}

        <FaArrowRight />
      </button>
    </div>
  );
}

function RegisterLoginLink({ t }) {
  return (
    <div className="register-login-row">
      <Link
        to="/"
        className="login-link"
      >
        <FaSignInAlt />

        {t(
          'register.backToLogin',
          'Back to login'
        )}
      </Link>
    </div>
  );
}

export default Register;